// ABOUTME: Dialog to save the current configurator snapshot as a reusable configuration template.
// ABOUTME: Captures title, description and visibility tier, then calls the templates REST hook.

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@ds/coolshop';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { TemplateVisibility, useTemplates } from '../../hooks/useTemplates';

interface SaveTemplateDialogProps {
    open: boolean;
    configurationJson: string;
    prototypeId: string;
    onClose: () => void;
    onSaved?: () => void;
}

export function SaveTemplateDialog({
    open,
    configurationJson,
    prototypeId,
    onClose,
    onSaved,
}: SaveTemplateDialogProps) {
    const { formatMessage } = useIntl();
    const { createTemplate, loading } = useTemplates();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState<TemplateVisibility>('private');

    useEffect(() => {
        if (!open) return;
        setTitle('');
        setDescription('');
        setVisibility('private');
    }, [open]);

    const isValid = title.trim().length > 0;

    const handleSubmit = async () => {
        if (!isValid) return;
        try {
            await createTemplate({
                title: title.trim(),
                description: description.trim() || undefined,
                visibility,
                configurationJson,
                prototypeId,
            });
            SnackBarUtils.success(
                formatMessage({
                    id: 'cpq.save-template.success',
                    defaultMessage: 'Template saved successfully',
                }),
            );
            onSaved?.();
            onClose();
        } catch {
            SnackBarUtils.error(
                formatMessage({
                    id: 'generic-error',
                    defaultMessage: 'An unexpected error occurred.',
                }),
            );
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h4">
                    {formatMessage({
                        id: 'cpq.save-template.title',
                        defaultMessage: 'Save as template',
                    })}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box paddingTop={1}>
                    <Stack gap={2}>
                        <TextField
                            label={formatMessage({
                                id: 'cpq.save-template.name',
                                defaultMessage: 'Title',
                            })}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label={formatMessage({
                                id: 'cpq.save-template.description',
                                defaultMessage: 'Description',
                            })}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            fullWidth
                            multiline
                        />
                        <Select
                            label={formatMessage({
                                id: 'cpq.save-template.visibility',
                                defaultMessage: 'Visibility',
                            })}
                            value={visibility}
                            onChange={e => setVisibility(e.target.value as TemplateVisibility)}
                            fullWidth
                        >
                            <MenuItem value="private">
                                {formatMessage({
                                    id: 'cpq.save-template.visibility.private',
                                    defaultMessage: 'Private',
                                })}
                            </MenuItem>
                            <MenuItem value="distributorship">
                                {formatMessage({
                                    id: 'cpq.save-template.visibility.distributorship',
                                    defaultMessage: 'Distributorship',
                                })}
                            </MenuItem>
                            {/* 'market' is deferred to a later release. */}
                            <MenuItem value="market" disabled>
                                {formatMessage({
                                    id: 'cpq.save-template.visibility.market',
                                    defaultMessage: 'Market (coming soon)',
                                })}
                            </MenuItem>
                        </Select>
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={onClose} disabled={loading}>
                    {formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || loading}>
                    {formatMessage({ id: 'common.save', defaultMessage: 'Save' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
